import map from "lodash/map";
import { Fragment, ReactElement, ReactNode, Suspense } from "react";
import { useAuth } from "react-oidc-context";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { NotFound } from "./NotFound";
import { routes } from "./routes";
import { Template } from "./Template";
import { LazyAggregationBackgroundRefetch } from "@/frontend/aggregation/lazyComponents";
import { PreloadAssessments } from "@/frontend/assessment/PreloadAssessments";
import { LazyEvaluationBackgroundRefetch } from "@/frontend/evaluation/lazyComponents";
import { useFirmId } from "@/frontend/firm/FirmProvider";
import { LazyPrepopulationBackgroundRefetch } from "@/frontend/prepopulation/lazyComponents";
import { AutoLogout } from "@/frontend/user/AutoLogout";
import { LazyNoFirmPage } from "@/frontend/user/lazyUserPages";
import { useLoggedInUser } from "@/frontend/user/LoggedInUserProvider";
import { UserThemeSetter } from "@/frontend/user/UserThemeSetter";

export const mappedRoutes = map(routes, x => x);

export const Router = () => {
    return (
        <Routes>
            <Route path="/logout" element={<AutoLogout />} />
            <Route element={<ProtectedTemplate />}>
                {mappedRoutes.map(({ path, render, allowAnyone }) => (
                    <Route
                        key={path}
                        path={path}
                        element={
                            <ParamsRoute
                                allowAnyone={allowAnyone ?? false}
                                render={render}
                            />
                        }
                    />
                ))}
                <Route path="*" element={<NotFound />} />
            </Route>
        </Routes>
    );
};

const ProtectedTemplate = () => {
    const { isAuthenticated } = useAuth();
    const { hasFirmId } = useFirmId();

    if (!isAuthenticated) {
        // shouldn't have got this far, but just in case
        return null;
    }

    if (!hasFirmId) {
        return <LazyNoFirmPage />;
    }

    return (
        <>
            <Template />
            <BackgroundDataHandling />
        </>
    );
};

const BackgroundDataHandling = () => {
    return (
        <>
            {/* UserThemeSetter not inside Suspense so that top level loading boundary still shows before a possible theme switch */}
            <UserThemeSetter />
            {/* Background refetches / preloading */}
            <Suspense>
                <PreloadAssessments />
                <LazyPrepopulationBackgroundRefetch />
                <LazyEvaluationBackgroundRefetch />
                <LazyAggregationBackgroundRefetch />
            </Suspense>
        </>
    );
};

const ParamsRoute = ({
    allowAnyone,
    render
}: {
    allowAnyone: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    render: (params: any, props: any) => ReactElement;
}) => {
    const params = useParams();

    // ensure that the route is re-rendered when the params change
    const key = JSON.stringify(params);

    const element = render(params, undefined);

    return (
        <Fragment key={key}>
            {allowAnyone ? (
                element
            ) : (
                <ProtectedSuperRoute>{element}</ProtectedSuperRoute>
            )}
        </Fragment>
    );
};

const ProtectedSuperRoute = ({ children }: { children: ReactNode }) => {
    const { isSuper } = useLoggedInUser();
    return isSuper ? children : <Navigate to="/" replace />;
};
